import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageCircle, Smartphone, Mail, Copy, Loader2, ShieldCheck } from "lucide-react";

const CHANNELS = [
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, tid: "outreach-whatsapp-btn" },
  { id: "sms", label: "SMS", icon: Smartphone, tid: "outreach-sms-btn" },
  { id: "email", label: "Email", icon: Mail, tid: "outreach-email-btn" },
];

export default function OutreachModal({ open, onOpenChange, patient }) {
  const [channel, setChannel] = useState("whatsapp");
  const [loading, setLoading] = useState(false);
  const [copy, setCopy] = useState(null);
  const [sending, setSending] = useState(false);

  const gen = async (ch) => {
    setChannel(ch);
    setLoading(true);
    setCopy(null);
    try {
      const { data } = await api.post(`/retention/${patient.cliniko_id}/outreach`, { channel: ch, reason: patient.reason });
      setCopy(data.copy);
    } catch { toast.error("Could not draft outreach — retry"); }
    finally { setLoading(false); }
  };

  const doCopy = async () => {
    const text = (copy?.subject ? `Subject: ${copy.subject}\n\n` : "") + (copy?.body || "");
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Outreach copy copied to clipboard — Clinician reviewed");
    } catch {
      toast.error("Couldn't copy automatically — select the text and copy manually");
    }
  };

  const waOpen = () => {
    const digits = (patient?.phone || "").replace(/[^0-9]/g, "");
    if (!digits) { toast.error("No phone number on file for this patient"); return; }
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(copy?.body || "")}`, "_blank");
  };

  const sendEmail = async () => {
    if (!patient?.email) { toast.error("No email on file for this patient"); return; }
    if (!copy) { toast.error("Draft the email first"); return; }
    if (!window.confirm(`Send this email directly to ${patient.name} (${patient.email})?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/retention/${patient.cliniko_id}/nudge`, { reason: patient.reason, subject: copy.subject, body: copy.body });
      toast.success(`Email sent to ${data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send email"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="outreach-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Draft outreach · {patient?.name}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            AI-drafted — edit the text below, then copy it or send the email directly.
          </DialogDescription>
        </DialogHeader>
        <p className="-mt-1 text-xs text-slate-500">{patient?.reason}</p>

        <div className="flex gap-2">
          {CHANNELS.map((c) => (
            <button key={c.id} onClick={() => gen(c.id)} data-testid={c.tid}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${channel === c.id ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              <c.icon className="h-4 w-4" /> {c.label}
            </button>
          ))}
        </div>

        <div className="min-h-[180px] rounded-xl border border-slate-200 bg-slate-50 p-4">
          {loading && <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /> Drafting {channel} copy...</div>}
          {!loading && copy && (
            <div className="space-y-2" data-testid="outreach-copy-text">
              {channel === "email" && (
                <input value={copy.subject || ""} onChange={(e) => setCopy({ ...copy, subject: e.target.value })} data-testid="outreach-subject-input"
                  placeholder="Subject" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium focus:border-teal-500 focus:outline-none" />
              )}
              <textarea value={copy.body || ""} onChange={(e) => setCopy({ ...copy, body: e.target.value })} data-testid="outreach-body-input" rows={7}
                className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed text-slate-700 focus:border-teal-500 focus:outline-none" />
            </div>
          )}
          {!loading && !copy && <div className="flex h-40 items-center justify-center text-sm text-slate-400">Choose a channel to draft copy.</div>}
        </div>

        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[11px] text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-teal-600" /> Draft only — you review &amp; send.</span>
          <div className="flex gap-2">
            {channel === "whatsapp" && (
              <Button onClick={waOpen} disabled={!copy} variant="outline" data-testid="outreach-whatsapp-send-btn" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                <MessageCircle className="mr-1.5 h-4 w-4" /> Open in WhatsApp
              </Button>
            )}
            {channel === "email" && (
              <Button onClick={sendEmail} disabled={sending || !copy || !patient?.email} variant="outline" data-testid="outreach-email-send-btn" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />} Send email
              </Button>
            )}
            <Button onClick={doCopy} disabled={!copy} data-testid="outreach-copy-clipboard-btn" className="bg-teal-600 hover:bg-teal-700">
              <Copy className="mr-1.5 h-4 w-4" /> Copy
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
